'use client';

import React, { useMemo } from 'react';
import Link from 'next/link';
import { converters } from '@/lib/converters';
import { categories } from '@/lib/data/categories';

interface ConversionUsage {
  from: string;
  to: string;
  count: number;
}

interface PopularConversionsProps {
  popularConversions: ConversionUsage[];
}

export function PopularConversions({ popularConversions }: PopularConversionsProps) {
  // Static base conversions for SEO as requested by the user
  const baseConversions = [
    { from: 'Centimeter', to: 'Inch', converterId: 'length' },
    { from: 'Kilogram', to: 'Pound', converterId: 'weightAndMass' },
    { from: 'Celsius', to: 'Fahrenheit', converterId: 'temperature' },
    { from: 'Millimeter', to: 'Inch', converterId: 'length' },
    { from: 'Meter', to: 'Foot', converterId: 'length' },
    { from: 'Kilometer', to: 'Mile', converterId: 'length' },
    { from: 'Gram', to: 'Ounce', converterId: 'weightAndMass' },
    { from: 'Inch', to: 'Foot', converterId: 'length' },
    { from: 'Liter', to: 'Gallon', converterId: 'volume' },
    { from: 'Pound', to: 'Ounce', converterId: 'weightAndMass' },
    { from: 'Mile per Hour', to: 'Kilometer per Hour', converterId: 'speed' },
    { from: 'Acre', to: 'Square Foot', converterId: 'area' },
    { from: 'Radian', to: 'Degree', converterId: 'angle' },
    { from: 'Horsepower', to: 'Kilowatt', converterId: 'power' },
    { from: 'Meter', to: 'Yard', converterId: 'length' },
    { from: 'Milliliter', to: 'Cup', converterId: 'volume' },
  ];

  const getUnitName = (converterId: string, unitSymbol: string): string => {
    const unit = converters[converterId]?.units.find((u) => u.symbol === unitSymbol || u.name === unitSymbol);
    return unit ? unit.name : unitSymbol;
  };

  const allConversions = useMemo(() => {
    const dynamic = popularConversions.map(c => {
      let converterId = Object.entries(converters).find(([_, conv]) =>
        conv.units.some((u) => u.symbol === c.from) &&
        conv.units.some((u) => u.symbol === c.to)
      )?.[0];
      
      if (!converterId) {
        converterId = Object.entries(converters).find(([_, conv]) =>
          conv.units.some((u) => u.symbol === c.from)
        )?.[0];
      }
      
      return { 
        from: getUnitName(converterId || 'length', c.from), 
        to: getUnitName(converterId || 'length', c.to), 
        converterId: converterId || 'length' 
      };
    });

    // Merge static and dynamic, avoid duplicates
    const seen = new Set();
    const result: { from: string, to: string, converterId: string }[] = [];

    [...dynamic, ...baseConversions].forEach(c => {
      const key = `${c.from}-${c.to}`;
      if (!seen.has(key)) {
        seen.add(key);
        result.push(c);
      }
    });

    return result.slice(0, 30); // Show top 30
  }, [popularConversions]);

  return (
    <div className="flex flex-wrap gap-2">
      {allConversions.map((conversion, idx) => {
        const converter = converters[conversion.converterId];
        const category = categories.find(cat => cat.converters.includes(conversion.converterId));
        const categorySlug = category?.name.toLowerCase().replace(/\s+/g, '-');
        const converterSlug = converter?.name.toLowerCase().replace(/\s+/g, '-');

        return (
          <Link
            href={categorySlug && converterSlug ? `/${categorySlug}/${converterSlug}?from=${encodeURIComponent(conversion.from)}&to=${encodeURIComponent(conversion.to)}` : '#'}
            key={idx}
            className="px-3 py-1.5 rounded-full border border-emerald-100 bg-emerald-50 text-emerald-700 hover:bg-emerald-600 hover:text-white hover:border-emerald-600 transition-all text-xs font-medium"
          >
            {conversion.from} to {conversion.to}
          </Link>
        );
      })}
    </div>
  );
}
